import { getField, updateField } from 'vuex-map-fields'
import { getNewId } from '@/utils/id'
import BaseStore from './baseStore'
import { generateSingleItemOverview } from '@/store/baseOverview'
import api from '../api/api'
import { toRaw } from 'vue'

const baseOverview = generateSingleItemOverview({
  itemFilter: (item, stabSchematisationStage) =>
    item.properties.stabSchematisationStageId === stabSchematisationStage.id
})

export default class {
  constructor(baseUrl) {
    const baseStore = new BaseStore(baseUrl)

    const getDefaultState = () => {
      return {
        ...structuredClone(baseStore.state),
        generateLoading: false,
        deletedItem: {},
        coordinates: [],
        delete: false,
        deletedCoordinates: [],
        dirtyObject: {}
      }
    }

    this.state = {
      ...getDefaultState(),
      ...structuredClone(baseOverview.state)
    }

    this.mutations = {
      ...baseStore.mutations,
      ...baseOverview.mutations,
      RESET_STATE(state) {
        Object.assign(state, getDefaultState())
      },
      REPLACE_LINE(state, item) {
        let index = state.items.findIndex((line) => line.id === item.id)
        state.items[index] = item
        state.currentItem = state.items[index]
      },
      TOGGLE_DELETE(state) {
        state.delete = !state.delete
      },
      SET_COORDINATE(state) {
        if (state.currentItem) {
          state.coordinates = state.currentItem.geometry.coordinates.map((item, index) => {
            return { id: -index, coordinate: item }
          })
        }
      },
      SET_DELETED_COORDINATE(state) {
        if (state.deletedItem) {
          state.deletedCoordinates = state.deletedItem.geometry.coordinates.map((item, index) => {
            return { id: -index, coordinate: item }
          })
        }
      },
      SET_DELETED_ITEM(state, item) {
        state.deletedItem = item
      },
      DELETE_COORDINATE(state, id) {
        const coordinate = state.deletedCoordinates.find((item) => item.id === id)
        if (coordinate) {
          const index = state.deletedItem['geometry']['coordinates'].findIndex(
            (item) => coordinate['coordinate'] === item
          )
          if (index !== -1 && state.deletedItem['geometry']['coordinates'].length > 2) {
            state.delete = true
            state.deletedItem['geometry']['coordinates'].splice(index, 1)
            state.deletedCoordinates.splice(
              state.deletedCoordinates.findIndex((item) => item.id === id),
              1
            )
          }
        }
      },
      SET_DIRTY_OBJECT(state) {
        state.dirtyObject = state.currentItem
      },
      EMPTY_DIRTY_OBJECT(state) {
        state.dirtyObject = {}
      },
      ADD_LINE_ITEM(state, { id, item, index }) {
        state.currentItem['geometry']['coordinates'].splice(index, 0, item)
        state.coordinates.splice(index, 0, { id: id, coordinate: item })
      },
      DELETE_LINE_ITEM(state, index) {
        state.currentItem['geometry']['coordinates'].splice(index, 1)
        state.coordinates.splice(index, 1)
        state.count -= 1
      },
      SET_COUNT(state, count) {
        state.count = count
      },
      REPLACE_CURRENT_ITEM(state, createdObj) {
        state.currentItem = createdObj
      },
      TOGGLE_GENERATE_LOADING(state) {
        state.generateLoading = !state.generateLoading
      },
      SET_OVERVIEW_ITEM(state, payload) {
        state.overviewItems.push(payload)
      },
      SORT_COORDINATES(state) {
        state.currentItem['geometry']['coordinates'] = state.currentItem['geometry'][
          'coordinates'
        ].sort((a, b) => a[0] - b[0])
        state.coordinates = state.coordinates.sort((a, b) => a.coordinate[0] - b.coordinate[0])
      },
      updateField
    }
    this.actions = {
      ...baseStore.actions,
      ...baseOverview.actions,
      resetState({ commit }) {
        commit('RESET_STATE')
      },
      sortCoordinatesX({ commit }) {
        commit('SORT_COORDINATES')
      },

      async fetchItem(context, id) {
        let url = context.state.baseUrl + `${id}/`
        let response = await api.get(url)
        context.commit('ADD_ITEM', response.data)
        context.commit('SET_CURRENT_ITEM', response.data)
        context.commit('SET_DELETED_ITEM', {
          ...structuredClone(response.data)
        })
        context.commit('SET_COORDINATE')
        context.commit('SET_DELETED_COORDINATE')
      },
      async fetchItemOverview(
        { state, commit },
        { id, stabSchematisationStageId, number, enabled = true }
      ) {
        if (state.itemsOverview.findIndex((item) => item.id === id) < 0) {
          const url = `${state.baseUrl}${id}/`
          const response = await api.get(url)
          response.data.properties.stabSchematisationStageId = stabSchematisationStageId
          response.data.properties.enabled = enabled
          if (Number.isInteger(number)) {
            response.data.properties.number = number
          }
          commit('ADD_ITEM_OVERVIEW', response.data)
        }
      },
      async fetchItems(context, { id, currentId = -1 }) {
        let url = baseUrl + `?search=${id}`
        context.commit('RESET_STATE')
        let response = await api.get(url)
        context.commit('SET_ITEMS', response.data['results']['features'])
        let index = 0
        if (currentId !== -1) {
          index = context.state.items.findIndex((item) => {
            return item.id === currentId
          })
        }
        context.commit('SET_CURRENT_ITEM', context.state.items[index])
        context.commit('SET_DELETED_ITEM', {
          ...structuredClone(toRaw(context.state.items[index]))
        })
        context.commit('ADD_COUNT', response.data['count'])
        context.commit('SET_COORDINATE')
        context.commit('SET_DELETED_COORDINATE')
      },
      async setCurrentItem(context, payload) {
        context.commit('SET_CURRENT_ITEM', payload)
        context.commit('SET_DELETED_ITEM', { ...structuredClone(toRaw(payload)) })
        context.commit('SET_COORDINATE')
        context.commit('SET_DELETED_COORDINATE')
      },
      setNestedParameter(context, payload) {
        context.commit('SET_NESTED_PARAMETER', payload)
        context.commit('ADD_DIRTY_ITEM', payload.item)
        context.commit('SET_DIRTY_OBJECT')
      },
      async add(context, index = null) {
        if (!context.state.currentItem) {
          return
        }
        let coordinates = [0, 0]
        if (index !== null) {
          coordinates = [...context.state.currentItem['geometry']['coordinates'][index]]
          index += 1 // add after the selected index
        } else {
          coordinates = [...context.state.currentItem['geometry']['coordinates'][0]]
          index = 0 // add at the beginning
        }
        let ids = getNewId(context.state.coordinates, 'id')
        context.commit('ADD_LINE_ITEM', {
          id: ids,
          item: coordinates,
          index: index
        })
        context.commit('SET_DIRTY_OBJECT')
        context.commit('ADD_DIRTY_ITEM', { id: ids, coordinate: coordinates })
        context.commit('ADD_COUNT', 1)
      },
      async deleteItem(context, { index, id }) {
        if ('id' in context.state.deletedItem) {
          let url = baseUrl + `${context.state.deletedItem['id']}/`

          context.commit('DELETE_COORDINATE', id)
          if (context.state.delete) {
            try {
              await api.patch(url, context.state.deletedItem)
              context.commit('DELETE_LINE_ITEM', index)
              context.commit('TOGGLE_DELETE')
            } catch (error) {
              console.log(error)
            }
          } else {
            if (context.getters.enableDelete(id)) {
              context.commit('DELETE_LINE_ITEM', index)
            }
          }
        }
      },
      async update(context) {
        let updatedObj = context.state.dirtyObject ? context.state.dirtyObject : {}
        if ('id' in updatedObj) {
          let url = baseUrl + `${updatedObj['id']}/`
          let response = await api.patch(url, updatedObj)
          context.commit('SET_CURRENT_ITEM', response.data)
          context.commit('SET_DELETED_ITEM', {
            ...structuredClone(response.data)
          })
          context.commit('SET_COORDINATE')
          context.commit('SET_DELETED_COORDINATE')
          context.commit('EMPTY_DIRTY_OBJECT')
        }
      },
      async generate(context, id) {},

      async replaceLine(context, response) {
        const { warnings = [], ...responseData } = response.data
        for (const message of warnings) {
          // bus.$emit('warning', message)
        }
        const topoProfileId = context.rootGetters['stabSchematisationStage/currentTopoProfileId']
        await context.dispatch('topoProfilePoint/fetchItems', topoProfileId, {
          root: true
        })

        context.commit('REPLACE_LINE', responseData)
        context.commit('SET_DELETED_ITEM', {
          ...structuredClone(responseData)
        })
        context.commit('SET_COORDINATE')
        context.commit('SET_DELETED_COORDINATE')
      }
    }

    this.getters = {
      ...baseOverview.getters,
      getField,
      enableDelete: (state) => (id) => {
        if (state.deletedCoordinates.length <= 2) {
          const coordinate = state.deletedCoordinates.find((item) => item.id === id)
          return coordinate ? false : true
        }
        return true
      },
      getPlotData:
        (state, getters) =>
        (stabSchematisationStage = null) => {
          let plotData = []
          let currentItem = getters.getItemByStabSchematisationStage(stabSchematisationStage)
          if (currentItem) {
            const singleLine = getters.getSingleLine(currentItem)
            plotData.push(singleLine)
          }
          return plotData
        },
      getMultiplePlotData:
        (state, getters) =>
        (stabSchematisationStage = null) => {
          if (!stabSchematisationStage) {
            return []
          }
          let plotData = []
          for (let item of state.itemsOverview) {
            if (item.properties.stabSchematisationStageId === stabSchematisationStage.id) {
              if (!item.properties.enabled && item.properties.number === 2) {
                continue
              }
              const singleLine = getters.getSingleLine(item, item.properties.number)
              plotData.push(singleLine)
            }
          }
          return plotData
        },
      getSingleLine:
        (state) =>
        (item, number = null) => {
          const coordinate = item.geometry.coordinates
          let singleLine = {
            x: [],
            y: [],
            line: { simplify: false },
            mode: 'lines+text+markers',
            hovertemplate: '(%{x}, %{y})<extra></extra>',
            type: 'scatter',
            name: null,
            textangle: null,
            textfont: {
              family: 'Roboto'
            }
          }
          singleLine.x = coordinate.map((item) => item[0])
          singleLine.y = coordinate.map((item) => item[1])
          singleLine.id = item.id
          singleLine.name = state.name + (number ? ` ${number}` : '')
          singleLine.storeNamespace = state.storeNamespace
          singleLine.line = {
            color: state.color,
            dash: state.dash,
            width: 2
          }
          singleLine.marker = {
            color: 'white',
            size: 4,
            line: {
              color: state.color,
              width: 1
            }
          }
          return singleLine
        }
    }
  }
}
